/**
 * 音视频素材时长读取与校验
 *
 * 项目构建链是 webpack 3 + babel 6（见 .babelrc：env + stage-2），
 * 因此本文件禁止使用 ES2020 语法（可选链 ?. 、空值合并 ?? 等）。
 *
 * 时长单位统一为「秒」：
 * - 后端 activityMaterialTypeRefs 的 minDuration / maxDuration 是字符串秒值（如 "180"）
 * - HTMLMediaElement.duration 是秒（浮点）
 * - 七牛 avinfo 的 audio/video.duration 是秒（浮点）
 */

var DURATION_TIMEOUT = 8000
var MEDIA_TYPES = ['Audio', 'Video']

/**
 * 该素材类型是否需要进行时长校验（仅音频、视频）
 */
export function isMediaType (type) {
  return MEDIA_TYPES.indexOf(type) > -1
}

/**
 * 把规则里的时长转成秒数，未配置返回 null（表示不校验这一项）
 */
export function toSeconds (val) {
  if (val === null || val === undefined || val === '') return null
  var num = parseFloat(val)
  return isNaN(num) ? null : num
}

function createObjectUrl (file) {
  if (window.URL && window.URL.createObjectURL) return window.URL.createObjectURL(file)
  if (window.webkitURL && window.webkitURL.createObjectURL) return window.webkitURL.createObjectURL(file)
  if (window.createObjectURL) return window.createObjectURL(file)
  return ''
}

function releaseObjectUrl (url) {
  if (!url) return
  try {
    if (window.URL && window.URL.revokeObjectURL) window.URL.revokeObjectURL(url)
    else if (window.webkitURL && window.webkitURL.revokeObjectURL) window.webkitURL.revokeObjectURL(url)
  } catch (e) {
  }
}

/**
 * 读取音视频时长（秒）
 *
 * 说明：旧的写法是 new Audio() 后死等 audio.duration，靠设置超大 currentTime 逼浏览器算时长，
 * 在 iOS / 微信 WebView 上常常拿不到，最终返回 -1 导致校验被整体跳过。
 * 这里改成 preload=metadata + loadedmetadata 事件 + 超时兜底。
 *
 * @param {File} file input 选择到的文件
 * @param {String} mediaType 'Audio' | 'Video'
 * @param {Number} timeout 超时毫秒
 * @returns {Promise<Number>} 时长（秒），读取失败返回 -1
 */
export function readMediaDuration (file, mediaType, timeout) {
  return new Promise(function (resolve) {
    var url = createObjectUrl(file)
    if (!url) {
      resolve(-1)
      return
    }
    var el = mediaType === 'Video' ? document.createElement('video') : document.createElement('audio')
    var finished = false

    function finish (duration) {
      if (finished) return
      finished = true
      clearTimeout(timer)
      el.removeEventListener('loadedmetadata', onLoadedMetadata)
      el.removeEventListener('error', onError)
      // 延迟释放：部分 iOS 机型过早 revoke 会触发 error
      setTimeout(function () {
        releaseObjectUrl(url)
      }, 3000)
      resolve(duration)
    }

    function onLoadedMetadata () {
      var duration = el.duration
      if (typeof duration === 'number' && isFinite(duration) && duration > 0) {
        finish(duration)
      } else {
        finish(-1)
      }
    }

    function onError () {
      finish(-1)
    }

    var timer = setTimeout(function () {
      finish(-1)
    }, timeout || DURATION_TIMEOUT)

    el.preload = 'metadata'
    el.addEventListener('loadedmetadata', onLoadedMetadata)
    el.addEventListener('error', onError)
    el.src = url
    try {
      el.load()
    } catch (e) {
    }
  })
}

/**
 * 校验时长是否满足规则
 * @returns {String} 空字符串表示通过，否则返回错误文案
 */
export function checkDuration (duration, rule, mediaType, durationCN) {
  if (!rule) return ''
  var min = toSeconds(rule.minDuration)
  var max = toSeconds(rule.maxDuration)
  if (min === null && max === null) return ''

  var label = mediaType === 'Video' ? '视频' : '音频'
  var currentCN = durationCN(duration)

  if (min !== null && duration < min) {
    return label + '时长要求最少' + durationCN(min) + '\n当前' + currentCN + '，请重新选择'
  }
  if (max !== null && duration > max) {
    return label + '时长要求最多' + durationCN(max) + '\n当前' + currentCN + '，请重新选择'
  }
  return ''
}

/**
 * 从七牛上传完成回调里取服务端解析的时长（秒）
 * 服务端解析，比前端解码可靠，作为兜底校验的数据来源
 * @returns {Number} 时长（秒），取不到返回 -1
 */
export function readQnDuration (result, mediaType) {
  if (!result || !result.avinfo) return -1
  var node = mediaType === 'Video'
    ? (result.avinfo.video || result.avinfo.audio)
    : result.avinfo.audio
  if (!node) return -1
  var duration = parseFloat(node.duration)
  return isNaN(duration) ? -1 : duration
}

/**
 * 该规则是否配置了时长限制
 * 没有配置时长限制时，读不到时长不需要报异常
 */
export function hasDurationRule (rule) {
  if (!rule) return false
  return toSeconds(rule.minDuration) !== null || toSeconds(rule.maxDuration) !== null
}

/**
 * 上传提示里的时长描述，如 3-5分钟以内 / 5分钟以内 / 最少3分钟
 * 没有时长限制时返回空字符串
 */
export function durationLimitCN (rule, durationCN) {
  if (!rule) return ''
  var min = toSeconds(rule.minDuration)
  var max = toSeconds(rule.maxDuration)
  if (min === null && max === null) return ''

  if (min !== null && max !== null) {
    if (min % 60 === 0 && max % 60 === 0) return (min / 60) + '-' + (max / 60) + '分钟以内'
    return durationCN(min) + '-' + durationCN(max) + '以内'
  }
  if (max !== null) {
    if (max % 60 === 0) return (max / 60) + '分钟以内'
    return durationCN(max) + '以内'
  }
  if (min % 60 === 0) return '最少' + (min / 60) + '分钟'
  return '最少' + durationCN(min)
}

/**
 * 生成上传提示文案
 * @param {String} tpl 有时长限制时的模板，含 {durationLimit} {maxSize}
 * @param {String} tplNoDuration 无时长限制时的模板，含 {maxSize}
 */
export function buildRuleStr (tpl, tplNoDuration, rule, durationCN) {
  if (!rule) return ''
  var limit = durationLimitCN(rule, durationCN)
  var maxSizeCN = (rule.maxSize === null || rule.maxSize === undefined) ? '不限' : rule.maxSize
  var template = limit ? tpl : tplNoDuration
  return template
    .replace('{durationLimit}', limit)
    .replace('{maxSize}', maxSizeCN)
}

/**
 * 无法确认时长时的兜底提示（前端与七牛都读不到）
 */
export function unknownDurationErr (mediaType) {
  var label = mediaType === 'Video' ? '视频' : '音频'
  return '无法读取该' + label + '的时长，\n请删除后更换' + label + '文件'
}



// WEBPACK FOOTER //
// ./src/util/media.js